/* ============================================================================
   CHANVRERY — CHAT « ERDY » : LE TRANSPORT
   ============================================================================

   SOURCE : design/REFERENCE.md (relevé Figma, page 221:6056), planches.json,
   chatbot/src/types.ts (ReponseSSE, EtatErreur), chatbot/src/http/validation.ts
   (ce que POST /chat et POST /evenement acceptent) et docs/CONTRAT.md §4.

   Familles couvertes ici : G1 à G5 et G9 côté réseau, D6/D7/D8 par délégation
   au module de rendu, F8/F9 par délégation au socle.

   RÔLE DE CE FICHIER
   Il parle au serveur, et rien d'autre. Il ne crée AUCUN nœud du DOM, il
   n'écrit AUCUN texte d'interface : tout ce qui s'affiche passe par
   `ChanvreryChat.module('rendu')`, tout ce qui se lit passe par
   `ChanvreryChat.textes()`. Cette séparation n'est pas cosmétique — c'est ce
   qui permet à un message d'erreur d'être choisi par le CODE à partir d'un
   état de planche, jamais construit à partir d'un message d'exception. L'audit
   (M3) montrait que le bot précédent laissait fuir des noms de fichiers et de
   framework par ce chemin-là.

   POURQUOI fetch + ReadableStream ET PAS EventSource
   EventSource ne sait faire que du GET : il ne peut pas porter le corps JSON
   qu'attend POST /chat. Il n'accepte pas non plus d'en-tête. Le prix à payer
   est le découpage manuel du flux, traité plus bas.

   ⚠️ L'EN-TÊTE `Origin` EST EXIGÉ, PAS SEULEMENT VÉRIFIÉ.
   src/http/serveur.ts refuse en 403 toute requête vers /chat ou /evenement qui
   arrive sans `Origin` figurant dans ORIGINE_AUTORISEE. Cet en-tête est
   interdit d'écriture depuis JavaScript : c'est le navigateur qui le pose, et
   il ne le pose que sur une vraie requête CORS. D'où deux conséquences tenues
   ici : jamais de `mode: 'no-cors'`, et un 403 est traité comme un défaut de
   configuration serveur (message explicite en console pour l'intégrateur),
   pas comme un incident visiteur.

   AUCUNE RELANCE AUTOMATIQUE D'UN TOUR DÉJÀ PARTI.
   Un tour coûte un appel au modèle. Rejouer un tour dont on ne sait pas s'il
   a été traité, c'est payer deux fois et risquer deux réponses. La seule
   reprise automatique est celle de la planche G1, où le message n'a
   littéralement PAS été envoyé — voir §6.
   ========================================================================= */

(function () {
  'use strict';

  var API = window.ChanvreryChat;

  /* Le socle n'a pas été évalué (asset non déployé, erreur de syntaxe) : sans
     lui il n'y a ni identifiants ni registre, donc rien à transporter. */
  if (!API) return;

  /* Le fichier peut être évalué deux fois si la section est dupliquée dans
     l'éditeur de thème. Un second transport doublerait chaque requête. */
  if (API.module('transport')) return;

  /* ==========================================================================
     1. CONSTANTES
     ====================================================================== */

  var CHEMIN_CHAT = '/chat';
  var CHEMIN_EVENEMENT = '/evenement';

  /**
   * Délai de garde d'INACTIVITÉ, réarmé à chaque événement reçu.
   *
   * Le serveur tient la même minuterie à 20 000 ms (serveur.ts,
   * DELAI_INACTIVITE_MS) et la planche G3 promet au visiteur « Aucune réponse
   * après 20 secondes ». On se place volontairement JUSTE en dessous : si le
   * serveur meurt sans rien écrire, ou si un proxy avale le flux, personne ne
   * renverra jamais le G3 du serveur et le panneau resterait en « Erdy
   * réfléchit… » indéfiniment. Couper à 18 s garantit que la planche s'affiche.
   *
   * Contrepartie assumée : un tour serveur exactement à la frontière est
   * interrompu par le client. Le serveur voit alors la socket se fermer et
   * abandonne son appel au modèle — le tour n'est pas facturé jusqu'au bout.
   */
  var DELAI_GARDE_MS = 18000;

  /**
   * Ré-examen de `navigator.onLine` pendant une attente de reconnexion.
   *
   * L'événement `online` n'est pas fiable derrière un portail captif : le
   * système se croit connecté avant que le trafic passe réellement. Le libellé
   * `textes().reconnexion` annonce « Nouvelle tentative de connexion dans 5 s… »
   * — cette minuterie est ce qui rend cette phrase vraie.
   */
  var DELAI_SONDAGE_MS = 5000;

  /**
   * Profondeur de la file d'attente (planche F8 : l'envoi est désactivé
   * pendant une réponse, mais un raccourci ou un appel programmatique peut
   * malgré tout demander un tour).
   *
   * Bornée : chaque entrée est un tour payant, et le serveur applique un quota
   * horaire (M1). Une boucle accidentelle ne doit pas le consommer.
   */
  var PROFONDEUR_FILE = 3;

  /**
   * Langues réellement acceptées par le serveur (types.ts, LANGUES_SUPPORTEES).
   * `data-langue` vient de `request.locale.iso_code` : sur une boutique
   * multilingue il peut valoir « de » ou « es », que le schéma Zod rejette en
   * 400. Le champ étant optionnel, on préfère l'omettre et laisser le serveur
   * redétecter plutôt que de faire échouer le tour entier.
   */
  var LANGUES_SERVEUR = ['fr', 'en', 'it'];

  /* Causes d'abandon. Elles voyagent dans une variable du tour plutôt que dans
     le signal : `AbortSignal.reason` n'existe pas avant Safari 15.4. */
  var ARRET_ANNULATION = 'annulation';
  var ARRET_DELAI = 'delai';

  /* Séparateur d'événements SSE. Les trois formes de la spécification sont
     acceptées : un proxy qui normalise les fins de ligne ne doit pas rendre le
     flux illisible. Pas de drapeau `g` — `exec` repart alors toujours de 0,
     ce qui est exactement ce qu'on veut sur un tampon qu'on rogne à chaque
     tour de boucle. */
  var SEPARATEUR = /\r\n\r\n|\n\n|\r\r/;

  /* ==========================================================================
     2. ÉTAT DU MODULE
     ====================================================================== */

  /** Tour en vol, ou `null`. Un seul à la fois — c'est la règle F8. */
  var tour = null;

  /** Textes en attente d'un tour, dans l'ordre d'arrivée. */
  var file = [];

  /**
   * Message retenu parce que le navigateur était hors ligne — planche G1 :
   * « Ton message n'a pas été envoyé · En attente de connexion… ».
   * La bulle client déjà dessinée est conservée avec lui : la reprise la
   * réutilise au lieu d'en dessiner une seconde.
   */
  var enAttente = null;

  var minuterieSondage = 0;

  /** Évite d'empiler les bandeaux G1 si `offline` se répète. */
  var horsLigneSignale = false;

  var connecte = navigator.onLine !== false;

  /* ==========================================================================
     3. OUTILLAGE
     ====================================================================== */

  /**
   * Appelle une méthode d'un autre module sans jamais laisser son échec
   * remonter jusqu'au réseau.
   *
   * Sans cette barrière, un bug d'affichage dans le module de rendu
   * ressemblerait à une panne serveur : il serait attrapé par le `catch` du
   * tour et afficherait un G2. On perdrait la vraie cause et on accuserait le
   * serveur à tort.
   */
  function appelerSur(cible, methode, arguments_) {
    if (!cible || typeof cible[methode] !== 'function') return undefined;
    try {
      return cible[methode].apply(cible, arguments_ || []);
    } catch (e) {
      console.error('[Chanvrery · chat] ' + methode + ' a échoué', e);
      return undefined;
    }
  }

  function rendu() {
    return API.module('rendu');
  }

  /** Origine du serveur, sans barre oblique finale (le socle l'a déjà rognée). */
  function base() {
    var reglages = API.reglages();
    return reglages && reglages.urlServeur ? reglages.urlServeur : '';
  }

  /**
   * Langue à transmettre, ou `undefined` si elle n'est pas servie.
   * Renvoyer `undefined` compte : le schéma est `.strict()`, une clé présente
   * avec une valeur invalide produit un 400.
   */
  function langue() {
    var reglages = API.reglages();
    var code = reglages && reglages.langue ? String(reglages.langue) : '';
    return LANGUES_SERVEUR.indexOf(code) === -1 ? undefined : code;
  }

  /**
   * Bandeau d'état affiché HORS d'un tour — planches G1 et G9, et échecs
   * survenus avant l'ouverture du flux.
   *
   * ⚠️ Convention choisie ici, à noter : `afficherEtat(etat, options)` n'a pas
   * de forme d'options fixée par le CONTRAT. Le transport passe
   * `{ repriseDansS: number|null }`, du même sens que le second argument de
   * `flux.echouer(etat, repriseDansS)`.
   */
  function etatHorsTour(code, repriseDansS) {
    appelerSur(rendu(), 'afficherEtat', [
      code,
      { repriseDansS: typeof repriseDansS === 'number' ? repriseDansS : null },
    ]);
  }

  /* ==========================================================================
     4. LECTURE DU FLUX SSE
     ======================================================================
     LE BUG CLASSIQUE, ET SON TRAITEMENT.
     Un `ReadableStream` livre des morceaux de RÉSEAU, pas des morceaux de
     protocole. Un événement de 300 octets arrive régulièrement coupé en deux
     par la fragmentation TCP, et deux petits événements arrivent tout aussi
     régulièrement collés dans le même morceau. Un analyseur qui traiterait
     chaque morceau isolément perdrait un fragment de réponse sur deux.

     D'où un tampon qui survit d'un morceau à l'autre, vidé uniquement sur une
     ligne vide complète. Même raison pour `TextDecoder({stream:true})` : un
     « é » occupe deux octets et peut lui aussi être coupé en deux.
     ====================================================================== */

  /**
   * Extrait le JSON d'un bloc d'événement.
   *
   * Le serveur n'écrit qu'une ligne `data:` et aucun champ `event:` — le
   * discriminant est la clé `type` du JSON (serveur.ts, `ecrireSSE`). On lit
   * malgré tout le format complet : les lignes de commentaire `:` servent de
   * battements de cœur à beaucoup de proxys, et les ignorer silencieusement
   * évite un flot de faux avertissements.
   */
  function analyserBloc(bloc) {
    var lignes = bloc.split(/\r\n|\n|\r/);
    var donnees = [];

    for (var i = 0; i < lignes.length; i++) {
      var ligne = lignes[i];
      if (ligne === '' || ligne.charAt(0) === ':') continue;

      var coupure = ligne.indexOf(':');
      var champ = coupure === -1 ? ligne : ligne.slice(0, coupure);
      if (champ !== 'data') continue;

      var valeur = coupure === -1 ? '' : ligne.slice(coupure + 1);
      /* Une seule espace après les deux-points fait partie du cadrage, pas de
         la donnée. Les suivantes, si elles existent, appartiennent au JSON. */
      if (valeur.charAt(0) === ' ') valeur = valeur.slice(1);
      donnees.push(valeur);
    }

    if (donnees.length === 0) return null;

    var brut = donnees.join('\n');
    if (!brut) return null;

    try {
      return JSON.parse(brut);
    } catch (e) {
      /* Bloc illisible : on saute l'événement plutôt que d'abattre le tour.
         Le texte de l'exception ne sort pas du fichier — il nommerait la pile. */
      console.error('[Chanvrery · chat] événement SSE illisible', e);
      return null;
    }
  }

  function creerAnalyseur(surEvenement) {
    var tampon = '';

    return {
      pousser: function (texte) {
        if (!texte) return;
        tampon += texte;

        var trouve;
        while ((trouve = SEPARATEUR.exec(tampon)) !== null) {
          var bloc = tampon.slice(0, trouve.index);
          tampon = tampon.slice(trouve.index + trouve[0].length);

          var evenement = analyserBloc(bloc);
          if (evenement) surEvenement(evenement);
        }
      },

      /**
       * Le serveur termine toujours par une ligne vide, mais un proxy peut
       * couper juste avant. On tente le dernier bloc : s'il est tronqué, le
       * JSON échoue et l'événement est simplement ignoré.
       */
      terminer: function () {
        if (!tampon) return;
        var reste = tampon;
        tampon = '';
        var evenement = analyserBloc(reste);
        if (evenement) surEvenement(evenement);
      },
    };
  }

  /* ==========================================================================
     5. UN TOUR
     ====================================================================== */

  /**
   * Traduit un échec de transport en état de planche.
   *
   * ⚠️ N'est utilisé QUE pour les échecs de transport. Dès que le serveur a
   * parlé, c'est SON code qui fait foi : l'événement SSE `erreur` porte déjà
   * un `etat` de la famille G, et le deviner à partir du statut HTTP
   * produirait un état différent de celui que le serveur a journalisé.
   */
  function etatDepuisReponse(reponse) {
    if (reponse.status === 403) {
      /* Le serveur a écarté l'origine. C'est une erreur de déploiement, pas un
         incident : sans ce message, la panne se présente comme un chat muet et
         se cherche pendant des heures. Le visiteur, lui, voit un G2 — il n'a
         que faire de la liste blanche du serveur. */
      console.error(
        '[Chanvrery · chat] 403 origine_refusee — ajouter ' +
          window.location.origin +
          " à ORIGINE_AUTORISEE sur le serveur de chat.",
      );
      return 'G2_erreur_serveur';
    }

    /* 429 : le serveur applique normalement son quota DANS le flux (planche
       G4, avec décompte). Un 429 sec vient donc d'un intermédiaire — CDN,
       pare-feu applicatif — et mérite la même planche. */
    if (reponse.status === 429) return 'G4_trop_de_messages';

    /* 400 : le widget a mal formé sa requête (identifiant non UUID, message
       vide, clé en trop). C'est notre bug, jamais celui du visiteur. */
    if (reponse.status === 400) {
      console.error('[Chanvrery · chat] 400 requete_invalide sur ' + CHEMIN_CHAT);
    }

    return 'G2_erreur_serveur';
  }

  /** Secondes de reprise annoncées par un 429 d'intermédiaire, si lisibles. */
  function repriseDepuisEnTete(reponse) {
    var brut = reponse.headers ? reponse.headers.get('retry-after') : null;
    var secondes = brut === null ? NaN : parseInt(brut, 10);
    return isNaN(secondes) || secondes < 0 ? null : secondes;
  }

  /**
   * Orchestre un tour complet — c'est la séquence de docs/CONTRAT.md §4, et
   * personne d'autre ne la tient.
   *
   * @param {string} texte             message déjà rogné et borné par le socle
   * @param {HTMLElement} [bulleReprise] bulle client déjà à l'écran (reprise G1)
   */
  function lancerTour(texte, bulleReprise) {
    var moteur = rendu();

    /* 2. La bulle client apparaît AVANT l'appel réseau : la planche D7 montre
       un message « en cours d'envoi », pas un champ qui se vide dans le vide. */
    var bulle =
      bulleReprise ||
      appelerSur(moteur, 'ajouterMessage', [
        { role: 'client', texte: texte, etat: 'en-cours' },
      ]);

    /* 3. Le champ se vide puis se verrouille (F8). Dans cet ordre : verrouiller
       d'abord désactiverait le champ avant qu'on ait pu le vider. */
    API.viderSaisie();
    API.verrouillerSaisie('occupe');

    var controleur = typeof AbortController === 'function' ? new AbortController() : null;

    tour = {
      texte: texte,
      bulle: bulle,
      controleur: controleur,
      cause: null,
      minuterie: 0,
      flux: null,
      /* Sert au droit de rejouer : tant qu'aucun événement n'est arrivé, le
         serveur n'a rien produit et le tour peut être relancé sans risque de
         doublon. Voir §6. */
      recuQuelqueChose: false,
      termine: false,
    };
    var courant = tour;

    function armerGarde() {
      window.clearTimeout(courant.minuterie);
      courant.minuterie = window.setTimeout(function () {
        courant.cause = ARRET_DELAI;
        if (courant.controleur) courant.controleur.abort();
      }, DELAI_GARDE_MS);
    }

    /* 1 bis. Hors ligne : rien ne part. Le message est retenu, la bulle reste
       en « en cours » — c'est mot pour mot ce que montre la planche G1. */
    if (!connecte) {
      window.clearTimeout(courant.minuterie);
      tour = null;
      retenirHorsLigne(texte, bulle);
      return;
    }

    armerGarde();

    var analyseur = creerAnalyseur(function (evenement) {
      courant.recuQuelqueChose = true;
      armerGarde();
      distribuer(courant, evenement);
    });

    var corps = {
      id_conversation: API.idConversation(),
      id_visiteur: API.idVisiteur(),
      message: texte,
    };
    var codeLangue = langue();
    if (codeLangue) corps.langue = codeLangue;

    fetch(base() + CHEMIN_CHAT, {
      method: 'POST',
      /* `cors` explicite : c'est ce mode qui fait poser l'en-tête `Origin` par
         le navigateur, et le serveur répond 403 sans lui. `no-cors` rendrait
         d'ailleurs la réponse illisible. */
      mode: 'cors',
      /* Aucun cookie de la boutique ne part vers le serveur de chat : il n'en a
         pas l'usage, et il ne déclare pas `allow-credentials`. */
      credentials: 'omit',
      /* Un seul en-tête. `Accept` serait inutile — le serveur ne répond que du
         `text/event-stream` sur cette route — et tout en-tête hors liste sûre
         devrait être déclaré dans `access-control-allow-headers`, qui ne
         contient que `content-type`. */
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(corps),
      cache: 'no-store',
      signal: controleur ? controleur.signal : undefined,
    })
      .then(function (reponse) {
        if (courant.termine) return undefined;

        if (!reponse.ok) {
          /* 7. Échec avant le premier octet : la bulle passe en « échec »
             (planche D8) et l'état s'affiche en bandeau, hors flux. */
          appelerSur(rendu(), 'majMessage', [courant.bulle, 'echec']);
          var code = etatDepuisReponse(reponse);
          etatHorsTour(code, code === 'G4_trop_de_messages' ? repriseDepuisEnTete(reponse) : null);
          finirTour(courant);
          return undefined;
        }

        /* 5. Le serveur a pris le message : la bulle est « envoyée » (D6) et
           la réponse s'ouvre — l'indicateur de frappe (D3) part d'ici. */
        appelerSur(rendu(), 'majMessage', [courant.bulle, 'envoye']);
        courant.flux = appelerSur(rendu(), 'ouvrirReponse') || null;

        return lire(reponse, courant, analyseur);
      })
      .then(function () {
        if (courant.termine) return;

        analyseur.terminer();

        /* Flux clos sans `fin` ni `erreur` : réponse tronquée en route. Le
           serveur termine toujours par l'un des deux, donc quelque chose s'est
           interrompu entre lui et nous. */
        if (courant.flux) {
          appelerSur(courant.flux, 'echouer', ['G2_erreur_serveur', null]);
        }
        finirTour(courant);
      })
      .catch(function (erreur) {
        if (courant.termine) return;
        surEchec(courant, erreur);
      });
  }

  /** Boucle de lecture. Isolée pour garder `lancerTour` lisible. */
  function lire(reponse, courant, analyseur) {
    /* Repli sans streaming : quelques WebViews et certaines extensions
       remplacent `fetch` par une implémentation sans corps lisible. La réponse
       arrive alors d'un bloc — pas d'effet de frappe, mais un chat qui marche
       plutôt qu'un chat muet. */
    if (!reponse.body || typeof reponse.body.getReader !== 'function') {
      return reponse.text().then(function (texte) {
        courant.recuQuelqueChose = true;
        analyseur.pousser(texte);
      });
    }

    var lecteur = reponse.body.getReader();
    var decodeur = new TextDecoder('utf-8');

    function suivant() {
      return lecteur.read().then(function (bloc) {
        if (courant.termine) {
          try {
            lecteur.cancel();
          } catch (e) {
            /* Flux déjà clos : rien à annuler. */
          }
          return undefined;
        }

        if (bloc.done) {
          /* Vidange du décodeur : un caractère multi-octets à cheval sur la
             toute dernière frontière se perdrait sans cet appel. */
          analyseur.pousser(decodeur.decode());
          return undefined;
        }

        analyseur.pousser(decodeur.decode(bloc.value, { stream: true }));
        return suivant();
      });
    }

    return suivant();
  }

  /**
   * Aiguillage d'un événement du flux — `ReponseSSE` de types.ts.
   *
   * ⚠️ `outil.libelle` est affiché TEL QUEL. C'est un texte d'attente déjà
   * rédigé par le serveur (« Je regarde le catalogue… ») : aucun nom d'outil
   * n'en sort jamais, et rien ici ne doit tenter d'en déduire un — c'est le
   * traitement de R5, celui que la consigne de prompt seule n'avait pas tenu.
   */
  function distribuer(courant, evenement) {
    var flux = courant.flux;

    switch (evenement.type) {
      case 'delta':
        appelerSur(flux, 'delta', [evenement.texte]);
        return;

      case 'outil':
        appelerSur(flux, 'outil', [evenement.libelle]);
        return;

      case 'produits':
        appelerSur(flux, 'produits', [evenement.produits || []]);
        return;

      case 'fin':
        appelerSur(flux, 'terminer', [evenement.etat || null]);
        finirTour(courant);
        return;

      case 'erreur':
        /* Le code vient du serveur et de lui seul. On ne le reconstruit pas à
           partir du statut HTTP : le serveur répond 200 puis écrit son échec
           dans le flux (le refus de quota G4 en est l'exemple), et deviner
           depuis le statut donnerait un état faux. */
        appelerSur(flux, 'echouer', [
          evenement.etat,
          typeof evenement.reprise_dans_s === 'number' ? evenement.reprise_dans_s : null,
        ]);
        finirTour(courant);
        return;

      default:
        /* Type inconnu : le serveur a évolué avant le widget. On ignore, on ne
           casse pas la conversation en cours. */
        return;
    }
  }

  /** Traduit une exception de transport en planche. */
  function surEchec(courant, erreur) {
    var estAbandon = erreur && (erreur.name === 'AbortError' || courant.cause !== null);

    /* Annulation volontaire (fermeture d'onglet, C5) : le visiteur a demandé
       l'arrêt, il n'a pas à voir un message d'erreur pour autant. */
    if (courant.cause === ARRET_ANNULATION) {
      finirTour(courant, true);
      return;
    }

    var code;
    if (courant.cause === ARRET_DELAI) {
      code = 'G3_delai_depasse';
    } else if (!connecte || navigator.onLine === false) {
      code = 'G1_hors_ligne';
    } else if (estAbandon) {
      /* Abandon sans cause connue : le navigateur a coupé (onglet en arrière
         plan, mise en veille). Rien de mieux à dire qu'un incident. */
      code = 'G2_erreur_serveur';
    } else {
      /* `fetch` ne rejette que sur un échec réseau ou un blocage CORS. Le
         second se présente exactement comme le premier — d'où le message de
         console de `etatDepuisReponse`, qui reste la seule piste utile. */
      code = 'G2_erreur_serveur';
    }

    if (courant.flux) {
      appelerSur(courant.flux, 'echouer', [code, null]);
    } else {
      /* Rien n'était encore ouvert : la bulle client échoue (D8). */
      appelerSur(rendu(), 'majMessage', [courant.bulle, 'echec']);
      etatHorsTour(code, null);
    }

    /* Coupure réseau avant le moindre événement : le serveur abandonne son tour
       à la fermeture de la socket, donc rien n'a été produit et le message peut
       être rejoué sans doublon dès le retour de la connexion. */
    if (code === 'G1_hors_ligne' && !courant.recuQuelqueChose) {
      finirTour(courant, true);
      retenirHorsLigne(courant.texte, courant.bulle);
      return;
    }

    finirTour(courant);
  }

  /**
   * Clôt le tour et rend la main.
   *
   * @param {object} courant
   * @param {boolean} [sansSuite] ne pas enchaîner sur la file — utilisé quand
   *        le tour part en attente de reconnexion ou vient d'être annulé.
   */
  function finirTour(courant, sansSuite) {
    if (courant.termine) return;
    courant.termine = true;
    window.clearTimeout(courant.minuterie);

    if (tour === courant) tour = null;
    if (sansSuite) return;

    /* 8. Dans tous les cas, la saisie revient au visiteur — sauf si la file a
       déjà quelque chose à envoyer (on resterait alors verrouillé pour rien),
       ou si le réseau est tombé entre-temps (planche F9). */
    if (file.length > 0) {
      var suivant = file.shift();
      lancerTour(suivant);
      return;
    }

    if (!connecte) {
      API.verrouillerSaisie('hors_ligne');
      return;
    }

    API.deverrouillerSaisie();
  }

  /* ==========================================================================
     6. HORS LIGNE ET REPRISE — planches G1, F9, G9
     ======================================================================
     UNE SEULE RELANCE AUTOMATIQUE, ET ELLE EST SÛRE.
     Le message retenu ici n'est jamais parti : soit `navigator.onLine` était
     faux avant l'appel, soit `fetch` a échoué sans qu'un seul événement soit
     arrivé — auquel cas le serveur a vu la socket se fermer et a abandonné son
     tour (serveur.ts, écouteur `close`). Il n'y a donc ni réponse en double ni
     double facturation à craindre.

     Tout échec survenu APRÈS le premier événement reçu ne se rejoue pas : une
     réponse partielle a déjà été affichée, la rejouer la dédoublerait.
     ====================================================================== */

  function retenirHorsLigne(texte, bulle) {
    enAttente = { texte: texte, bulle: bulle || null };

    if (!horsLigneSignale) {
      horsLigneSignale = true;
      etatHorsTour('G1_hors_ligne', null);
    }

    API.verrouillerSaisie('hors_ligne');

    var textes = API.textes() || {};
    if (textes.reconnexion) API.annoncer(textes.reconnexion);

    demarrerSondage();
  }

  function demarrerSondage() {
    if (minuterieSondage) return;
    minuterieSondage = window.setInterval(function () {
      if (navigator.onLine === false) return;
      /* `online` n'est pas toujours émis (portail captif, reprise de veille) :
         c'est ce sondage qui fait repartir la conversation dans ces cas-là. */
      surReseau(true);
    }, DELAI_SONDAGE_MS);
  }

  function arreterSondage() {
    if (!minuterieSondage) return;
    window.clearInterval(minuterieSondage);
    minuterieSondage = 0;
  }

  function surReseau(enLigne) {
    connecte = enLigne;

    if (!enLigne) {
      /* Pas de bandeau si un tour est en vol : son échec va s'en charger, et
         deux G1 d'affilée dans le fil ne disent rien de plus qu'un seul. */
      if (tour) return;

      if (!horsLigneSignale) {
        horsLigneSignale = true;
        etatHorsTour('G1_hors_ligne', null);
      }
      API.verrouillerSaisie('hors_ligne');
      return;
    }

    arreterSondage();

    /* Planche G9 — « Connexion rétablie, Erdy a repris ». Uniquement si une
       coupure avait été annoncée : sinon le bandeau surgirait au premier
       `online` d'un navigateur qui vient de démarrer. */
    if (horsLigneSignale) {
      horsLigneSignale = false;
      etatHorsTour('G9_reprise', null);
    }

    API.annoncer('');

    if (enAttente) {
      var retenu = enAttente;
      enAttente = null;
      if (!tour) lancerTour(retenu.texte, retenu.bulle || undefined);
      return;
    }

    if (!tour) API.deverrouillerSaisie();
  }

  /* ==========================================================================
     7. INTERFACE PUBLIQUE — docs/CONTRAT.md §4
     ====================================================================== */

  var transport = {
    /**
     * Seul point d'entrée d'un échange.
     * @param {string} texte déjà rogné et borné à maxCaracteres par le socle
     */
    envoyer: function (texte) {
      var message = typeof texte === 'string' ? texte.trim() : '';
      if (!message) return;

      if (!base()) {
        /* Sans adresse la section ne se rend même pas ; on n'arrive ici que par
           un appel programmatique. Mieux vaut le dire que d'échouer en silence. */
        console.error("[Chanvrery · chat] aucune adresse de serveur : envoi impossible.");
        return;
      }

      /* Planche F8 — un tour à la fois. Le message n'est pas perdu, il attend
         son tour. La bulle n'est pas dessinée tout de suite : elle se placerait
         au-dessus d'une réponse encore en cours d'écriture. */
      if (tour || enAttente) {
        if (file.length < PROFONDEUR_FILE) file.push(message);
        return;
      }

      lancerTour(message);
    },

    /** Interrompt le tour en cours. Idempotent. */
    annuler: function () {
      file.length = 0;
      enAttente = null;
      arreterSondage();

      if (tour) {
        var courant = tour;
        courant.cause = ARRET_ANNULATION;

        /* Sans AbortController la requête suit son cours, mais le drapeau
           `termine` posé par finirTour fait que plus rien ne s'affichera. */
        if (courant.controleur) courant.controleur.abort();
        finirTour(courant, true);
      }

      /* Le verrou est levé ICI, pas dans le rejet de `fetch` : celui-ci arrive
         un tour de boucle plus tard, et le champ resterait grisé entre-temps. */
      if (connecte) API.deverrouillerSaisie();
      else API.verrouillerSaisie('hors_ligne');
    },

    /**
     * POST /evenement — ce que le navigateur est seul à savoir.
     *
     * Échec silencieux et volontaire : un compteur perdu est un pixel de
     * statistique, une exception remontée casserait un ajout au panier.
     *
     * @param {'conversation_ouverte'|'ajout_panier'} type
     */
    declarer: function (type) {
      if (type !== 'conversation_ouverte' && type !== 'ajout_panier') return;
      if (!base() || !connecte) return;

      var corps = { type: type, id_conversation: API.idConversation() };
      var codeLangue = langue();
      if (codeLangue) corps.langue = codeLangue;

      try {
        fetch(base() + CHEMIN_EVENEMENT, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(corps),
          /* `ajout_panier` précède souvent une navigation vers le panier :
             sans `keepalive`, la requête serait annulée au déchargement.
             `sendBeacon` ne conviendrait pas — il ne sait pas déclencher le
             préalable CORS qu'impose le type `application/json`. */
          keepalive: true,
        }).catch(function () {
          /* Silence assumé : cf. docs/CONTRAT.md §4. */
        });
      } catch (e) {
        /* Idem — un `fetch` qui lève à l'appel (URL invalide) ne doit rien
           interrompre. */
      }
    },

    /** @returns {boolean} */
    enLigne: function () {
      return connecte;
    },
  };

  API.enregistrer('transport', transport);

  /* ==========================================================================
     8. BRANCHEMENTS
     ====================================================================== */

  /* Le socle valide la forme puis émet : le transport est seul à réagir à
     `envoi` (docs/CONTRAT.md §3). */
  API.ecouter('envoi', function (detail) {
    transport.envoyer(detail && detail.texte);
  });

  API.ecouter('reseau', function (detail) {
    surReseau(!(detail && detail.enLigne === false));
  });

  /* Planche C5 — on repart de zéro : le tour en vol n'a plus de destinataire,
     et laisser sa réponse arriver dans un fil vidé serait déroutant. */
  API.ecouter('conversation-nouvelle', function () {
    transport.annuler();
  });

  /* `pret` est rémanent : ce fichier est évalué APRÈS que l'élément se soit
     connecté, l'abonnement rejoue donc l'événement déjà passé. C'est ici, et
     seulement ici, que l'état réseau initial devient certain — le socle a lu
     `navigator.onLine` au même moment. */
  API.ecouter('pret', function () {
    if (navigator.onLine === false) surReseau(false);
  });
})();
